// utils/getDamageTypeIcon.tsx
import { GameLog } from '../../../../Logs/LogsDisplay';

export const getDamageTypeIcon = (damageSubType: DamageSubType): JSX.Element | null => {
  switch (damageSubType) {
    case DamageSubType.Physical:
      return <div className="stat-icon attack-damage-icon"></div>;
    case DamageSubType.Magic:
      return <div className="stat-icon ability-power-icon"></div>;
    case DamageSubType.True:
      return <img src="/Images/trueDamageIcon.png" alt="True Damage" style={{ width: 17, height: 17, marginRight: 4 }} />;
    default:
      return null;
  }
};

export const getCritDamageTypeIcon = (
  damageSubType: DamageSubType,
  critIcons: { physical: string; magic: string; true: string }
): JSX.Element | null => {
  let iconSrc = '';
  switch (damageSubType) {
    case DamageSubType.Physical:
      iconSrc = critIcons.physical;
      break;
    case DamageSubType.Magic:
      iconSrc = critIcons.magic;
      break;
    case DamageSubType.True:
      iconSrc = critIcons.true;
      break;
    default:
      return null;
  }

  return <img src={iconSrc} alt={`${damageSubType} Crit Damage`} className="damage-icon" />;
};

export enum DamageType {
  Raw,
  DefaultPeriodic,
  Proc,
  Reactive,
  Basic,
  Spell,
  SpellAOE,
  SpellPersistent,
  PersistentAOE,
  Pet,

  IncreasedPercentageDamageTaken,
}

export enum DamageSubType {
  Physical = 0,
  Magic,
  True,
  AdaptiveDamage,
}

export interface StackUpdateGroup {
  keyPress: GameLog;        // The KeyPress event
  stackUpdates: GameLog[];  // Associated StackUpdate events
}

// utils/flattenLogs.ts
export const flattenLogs = (logs: GameLog[]): GameLog[] => {
  const flatLogs: GameLog[] = [];

  const traverse = (log: GameLog) => {
    flatLogs.push(log);
    if (log.childEvents && log.childEvents.length > 0) {
      log.childEvents.forEach(child => traverse(child));
    }
  };

  logs.forEach(log => traverse(log));
  return flatLogs;
};


export const extractStackUpdateGroups = (logs: GameLog[]): StackUpdateGroup[] => {
  const stackUpdateGroups: StackUpdateGroup[] = [];

  /**
   * Recursively traverses the logs to find StackUpdate events.
   *
   * @param events - The current array of GameLog events to process.
   * @param currentKeyPress - The current KeyPress event acting as the parent.
   */
  const traverse = (events: GameLog[], currentKeyPress: GameLog | null) => {
    events.forEach(event => {
      if (event.type === 'KeyPress') {
        // Found a KeyPress event; initialize a new group
        const newGroup: StackUpdateGroup = {
          keyPress: event,
          stackUpdates: [],
        };
        stackUpdateGroups.push(newGroup);

        // Traverse its child events with the new KeyPress as the parent
        if (event.childEvents && event.childEvents.length > 0) {
          traverse(event.childEvents, event);
        }
      } else if (event.type === 'StackUpdate' && currentKeyPress) {
        // Found a StackUpdate event; add it to the current group
        const currentGroup = stackUpdateGroups[stackUpdateGroups.length - 1];
        currentGroup.stackUpdates.push(event);

        // Continue traversing in case there are nested StackUpdate events
        if (event.childEvents && event.childEvents.length > 0) {
          traverse(event.childEvents, currentKeyPress);
        }
      } else {
        // For other event types, continue traversing without modifying the current KeyPress
        if (event.childEvents && event.childEvents.length > 0) {
          traverse(event.childEvents, currentKeyPress);
        }
      }
    });
  };

  // Start traversal with no current KeyPress
  traverse(logs, null);

  return stackUpdateGroups;
};
